"use client"

import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Badge } from "./ui/badge"
import { Button } from "./ui/button"
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "./ui/breadcrumb"
import {
    CheckCircle,
    XCircle,
    Clock,
    Play,
    BarChart3,
    ArrowLeft,
    User,
    Bot,
    Heart,
    BookMarked,
    Sparkles,
    TrendingUp,
    MessageSquare,
    Calendar,
    Timer,
    Brain,
    Target,
    RefreshCw,
} from "lucide-react"

// Importar tipos y transformer
import { MCPEvaluation, getEvaluationScore } from '../types'
import { DetailedEvaluation, transformToDetailedEvaluation, hasDetailedData } from '../utils/evaluationTransformer'

interface EvaluationDetailViewProps {
    evaluation: MCPEvaluation;
    onBack?: () => void;
    onToggleLike?: (id: string) => Promise<MCPEvaluation | undefined>;
    onToggleTraining?: (id: string) => Promise<MCPEvaluation | undefined>;
}

export default function EvaluationDetailView({
    evaluation: initialEvaluation,
    onBack,
    onToggleLike,
    onToggleTraining
}: EvaluationDetailViewProps) {
    const [evaluation, setEvaluation] = useState<DetailedEvaluation>(() => {
        // Si ya tiene datos detallados, los usa; si no, los transforma
        return hasDetailedData(initialEvaluation)
            ? initialEvaluation
            : transformToDetailedEvaluation(initialEvaluation);
    });

    const [loading, setLoading] = useState(false);

    // Actualizar cuando cambie la evaluación inicial
    useEffect(() => {
        setEvaluation(hasDetailedData(initialEvaluation)
            ? initialEvaluation
            : transformToDetailedEvaluation(initialEvaluation));
    }, [initialEvaluation]);

    const getStatusBadge = (status: string) => {
        const statusConfig: Record<string, { className: string; icon: JSX.Element }> = {
            completed: {
                className: "bg-green-100 text-green-800 border-green-200",
                icon: <CheckCircle className="w-3 h-3 mr-1" />,
            },
            failed: {
                className: "bg-red-100 text-red-800 border-red-200",
                icon: <XCircle className="w-3 h-3 mr-1" />,
            },
            pending: {
                className: "bg-yellow-100 text-yellow-800 border-yellow-200",
                icon: <Clock className="w-3 h-3 mr-1" />,
            },
            'in-progress': {
                className: "bg-blue-100 text-blue-800 border-blue-200",
                icon: <RefreshCw className="w-3 h-3 mr-1" />,
            },
            active: {
                className: "bg-blue-100 text-blue-800 border-blue-200",
                icon: <Play className="w-3 h-3 mr-1" />,
            },
        };

        const config = statusConfig[status] || statusConfig.completed;

        return (
            <Badge className={`flex items-center border ${config.className}`}>
                {config.icon}
                {status}
            </Badge>
        );
    };

    const getScoreColor = (score: number) => {
        if (score >= 8) return "text-green-600 bg-green-50 border-green-200"
        if (score >= 6) return "text-yellow-600 bg-yellow-50 border-yellow-200"
        return "text-red-600 bg-red-50 border-red-200"
    };

    const getScoreLabel = (score: number) => {
        if (score >= 8) return "Excelente"
        if (score >= 6) return "Mejorable"
        return "Necesita Mejora"
    };

    const handleToggleLike = async () => {
        if (onToggleLike) {
            setLoading(true);
            try {
                const updatedEvaluation = await onToggleLike(evaluation.id);
                if (updatedEvaluation) {
                    const detailedUpdated = hasDetailedData(updatedEvaluation)
                        ? updatedEvaluation
                        : transformToDetailedEvaluation(updatedEvaluation);
                    setEvaluation(detailedUpdated);
                }
            } catch (error) {
                console.error('Error toggling like:', error);
            } finally {
                setLoading(false);
            }
        } else {
            // Fallback local si no hay handler
            setEvaluation((prev) => ({ ...prev, is_liked: !prev.is_liked }));
        }
    };

    const handleToggleTraining = async () => {
        if (onToggleTraining) {
            setLoading(true);
            try {
                const updatedEvaluation = await onToggleTraining(evaluation.id);
                if (updatedEvaluation) {
                    const detailedUpdated = hasDetailedData(updatedEvaluation)
                        ? updatedEvaluation
                        : transformToDetailedEvaluation(updatedEvaluation);
                    setEvaluation(detailedUpdated);
                }
            } catch (error) {
                console.error('Error toggling training data:', error);
            } finally {
                setLoading(false);
            }
        } else {
            // Fallback local si no hay handler
            setEvaluation((prev) => ({ ...prev, is_training_data: !prev.is_training_data }));
        }
    };

    return (
        <div className="bg-gray-50 mt-0 pt-0 px-0">

            <div className="p-2 space-y-2 mt-0 pt-0">
                {/* Breadcrumbs */}
                <Breadcrumb>
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            <BreadcrumbLink
                                onClick={onBack}
                                className="flex items-center text-blue-600 hover:text-blue-700 cursor-pointer"
                            >
                                <ArrowLeft className="h-4 w-4 mr-1" />
                                Evaluaciones
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbPage className="font-medium">{evaluation.id}</BreadcrumbPage>
                        </BreadcrumbItem>
                    </BreadcrumbList>
                </Breadcrumb>

                {/* Header de la evaluación */}
                <Card className="border-0 shadow-sm">
                    <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg">
                                    <MessageSquare className="h-6 w-6 text-blue-600" />
                                </div>
                                <div>
                                    <CardTitle className="text-xl text-gray-900 flex items-center space-x-2">
                                        <span>Evaluación {evaluation.id}</span>
                                        {getStatusBadge(evaluation.status)}
                                    </CardTitle>
                                    <p className="text-gray-600 mt-1">
                                        {new Date(evaluation.timestamp).toLocaleString("es-ES", {
                                            year: "numeric",
                                            month: "long",
                                            day: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-3">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleToggleLike}
                                    disabled={loading}
                                    className={evaluation.is_liked ? "bg-pink-50 text-pink-700 border-pink-200" : ""}
                                >
                                    <Heart className={`h-4 w-4 mr-1 ${evaluation.is_liked ? "fill-current" : ""}`} />
                                    {evaluation.is_liked ? "Liked" : "Like"}
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleToggleTraining}
                                    disabled={loading}
                                    className={evaluation.is_training_data ? "bg-purple-50 text-purple-700 border-purple-200" : ""}
                                >
                                    <BookMarked className={`h-4 w-4 mr-1 ${evaluation.is_training_data ? "fill-current" : ""}`} />
                                    {evaluation.is_training_data ? "En Entrenamiento" : "Usar para Entrenamiento"}
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-6">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            <div className="flex items-center space-x-3">
                                <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
                                    <Target className="h-5 w-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Puntuación</p>
                                    <p className="text-lg font-bold text-gray-900">{getEvaluationScore(evaluation) || 'N/A'}/10</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-3">
                                <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
                                    <Brain className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Modelo</p>
                                    <p className="text-lg font-semibold text-gray-900">{evaluation.model || 'No especificado'}</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-3">
                                <div className="flex items-center justify-center w-10 h-10 bg-orange-100 rounded-lg">
                                    <Timer className="h-5 w-5 text-orange-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Duración</p>
                                    <p className="text-lg font-semibold text-gray-900">{evaluation.processing_time_ms}</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-3">
                                <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg">
                                    <BarChart3 className="h-5 w-5 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Rúbrica</p>
                                    <p className="text-lg font-semibold text-gray-900">{evaluation.rubric_type || evaluation.rubric_id || 'General'}</p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Conversación estilo chat */}
                <div className="space-y-6">
                    {/* Query del usuario */}
                    <div className="flex items-start space-x-4">
                        <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full flex-shrink-0">
                            <User className="h-5 w-5 text-blue-600" />
                        </div>
                        <Card className="flex-1 border-0 shadow-sm">
                            <CardHeader className="pb-3">
                                <div className="flex items-center space-x-2">
                                    <h3 className="font-semibold text-gray-900">Consulta del Usuario</h3>
                                    <Badge variant="outline" className="text-xs">
                                        <Calendar className="h-3 w-3 mr-1" />
                                        {new Date(evaluation.timestamp).toLocaleTimeString("es-ES", {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}
                                    </Badge>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{evaluation.query}</p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Respuesta del LLM */}
                    <div className="flex items-start space-x-4">
                        <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-full flex-shrink-0">
                            <Bot className="h-5 w-5 text-green-600" />
                        </div>
                        <Card className="flex-1 border-0 shadow-sm">
                            <CardHeader className="pb-3">
                                <div className="flex items-center space-x-2">
                                    <h3 className="font-semibold text-gray-900">Respuesta Generada</h3>
                                    <Badge variant="outline" className="text-xs">
                                        <Brain className="h-3 w-3 mr-1" />
                                        {evaluation.model || 'No especificado'}
                                    </Badge>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{evaluation.response}</p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Componente de evaluación */}
                    {evaluation.evaluation && (
                        <div className="flex items-start space-x-4">
                            <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-full flex-shrink-0">
                                <BarChart3 className="h-5 w-5 text-purple-600" />
                            </div>
                            <Card className="flex-1 border-0 shadow-sm bg-gradient-to-br from-slate-50 to-slate-100">
                                <CardContent className="p-6">
                                    <div className="space-y-6">
                                        {/* Score principal */}
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-3">
                                                <h3 className="text-lg font-bold text-gray-900">
                                                    Score: {evaluation.evaluation.overall_score}/10
                                                </h3>
                                                <Badge className={`px-3 py-1 ${getScoreColor(evaluation.evaluation.overall_score)}`}>
                                                    {getScoreLabel(evaluation.evaluation.overall_score)}
                                                </Badge>
                                            </div>
                                            <div className="flex space-x-2">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                                                >
                                                    Mejorable
                                                </Button>
                                                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                                                    <Sparkles className="h-4 w-4 mr-1" />
                                                    Mejorar Respuesta
                                                </Button>
                                            </div>
                                        </div>

                                        {/* Feedback */}
                                        <div>
                                            <div className="flex items-center space-x-2 mb-2">
                                                <MessageSquare className="h-4 w-4 text-gray-600" />
                                                <h4 className="font-semibold text-gray-900">Feedback:</h4>
                                            </div>
                                            <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{evaluation.evaluation.feedback}</p>
                                        </div>

                                        {/* Puntuación por criterios */}
                                        <div>
                                            <div className="flex items-center space-x-2 mb-4">
                                                <BarChart3 className="h-4 w-4 text-gray-600" />
                                                <h4 className="font-semibold text-gray-900">Puntuación por Criterios:</h4>
                                            </div>
                                            <div className="border rounded-lg overflow-hidden bg-white">
                                                <div className="grid grid-cols-3 gap-0 text-sm font-medium text-gray-700 bg-gray-50 border-b">
                                                    <div className="p-3 border-r">Criterio</div>
                                                    <div className="p-3 border-r text-center">Puntuación</div>
                                                    <div className="p-3 text-center">Peso</div>
                                                </div>
                                                {evaluation.evaluation.criteria_scores.map((criterion, index) => (
                                                    <div key={index} className="grid grid-cols-3 gap-0 text-sm border-b last:border-b-0">
                                                        <div className="p-3 border-r">
                                                            <div className="font-medium text-gray-900">{criterion.name}</div>
                                                            <div className="text-xs text-gray-500 mt-1">{criterion.description}</div>
                                                        </div>
                                                        <div className="p-3 border-r text-center">
                                                            <div className="flex items-center justify-center space-x-2">
                                                                <span className="font-bold text-gray-900">{criterion.score.toFixed(1)}/10</span>
                                                                <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                                                    <div
                                                                        className="h-full bg-blue-500 rounded-full transition-all duration-300"
                                                                        style={{ width: `${(criterion.score / 10) * 100}%` }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="p-3 text-center font-medium text-gray-700">{criterion.weight}%</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    {/* 🆕 Consulta Mejorada - Nueva sección */}
                    {evaluation.improved_query && (
                        <div className="flex items-start space-x-4">
                            <div className="flex items-center justify-center w-10 h-10 bg-emerald-100 rounded-full flex-shrink-0">
                                <Sparkles className="h-5 w-5 text-emerald-600" />
                            </div>
                            <Card className="flex-1 border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-green-50">
                                <CardHeader className="pb-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-2">
                                            <h3 className="font-semibold text-gray-900">Consulta Mejorada</h3>
                                            <Badge variant="outline" className="text-xs bg-emerald-100 text-emerald-700 border-emerald-200">
                                                <TrendingUp className="h-3 w-3 mr-1" />
                                                {evaluation.improvement_model || 'IA Generated'}
                                            </Badge>
                                        </div>
                                        {evaluation.improvement_timestamp && (
                                            <Badge variant="outline" className="text-xs">
                                                <Calendar className="h-3 w-3 mr-1" />
                                                {new Date(evaluation.improvement_timestamp).toLocaleString("es-ES", {
                                                    day: "2-digit",
                                                    month: "2-digit",
                                                    hour: "2-digit",
                                                    minute: "2-digit",
                                                })}
                                            </Badge>
                                        )}
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {/* Consulta mejorada */}
                                    <div>
                                        <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                                            <MessageSquare className="h-4 w-4 mr-2 text-emerald-600" />
                                            Versión Optimizada:
                                        </h4>
                                        <div className="bg-white p-4 rounded-lg border border-emerald-200">
                                            <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{evaluation.improved_query}</p>
                                        </div>
                                    </div>

                                    {/* Explicación de la mejora */}
                                    {evaluation.improvement_explanation && (
                                        <div>
                                            <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                                                <Brain className="h-4 w-4 mr-2 text-emerald-600" />
                                                Explicación de la Mejora:
                                            </h4>
                                            <div className="bg-white p-4 rounded-lg border border-emerald-200">
                                                <p className="text-gray-700 leading-relaxed">{evaluation.improvement_explanation}</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Comparación lado a lado */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                                        <div>
                                            <h5 className="font-medium text-gray-700 mb-2 text-sm">📝 Consulta Original:</h5>
                                            <div className="bg-gray-50 p-3 rounded-lg border text-sm">
                                                <p className="text-gray-600 italic">{evaluation.query}</p>
                                            </div>
                                        </div>
                                        <div>
                                            <h5 className="font-medium text-gray-700 mb-2 text-sm">✨ Consulta Mejorada:</h5>
                                            <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200 text-sm">
                                                <p className="text-emerald-800">{evaluation.improved_query}</p>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    {/* Texto de mejora */}
                    {evaluation.improvement_text && (
                        <div className="flex items-start space-x-4">
                            <div className="flex items-center justify-center w-10 h-10 bg-orange-100 rounded-full flex-shrink-0">
                                <TrendingUp className="h-5 w-5 text-orange-600" />
                            </div>
                            <Card className="flex-1 border-0 shadow-sm bg-gradient-to-br from-orange-50 to-yellow-50">
                                <CardHeader className="pb-3">
                                    <div className="flex items-center space-x-2">
                                        <h3 className="font-semibold text-gray-900">Sugerencias de Mejora</h3>
                                        <Badge variant="outline" className="text-xs bg-orange-100 text-orange-700 border-orange-200">
                                            <Sparkles className="h-3 w-3 mr-1" />
                                            IA Generated
                                        </Badge>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="prose prose-sm max-w-none">
                                        <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{evaluation.improvement_text}</p>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
} 